import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScoreRing } from "@/lib/match";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileText, ClipboardPaste, Sparkles, CheckCircle2, Pencil, Check, ArrowLeft, ArrowRight, Loader2, AlertTriangle, Search } from "lucide-react";

const STR_FIELDS = [
  ["primary_title", "Primary Job Title"], ["professional_category", "Professional Category"],
  ["description", "Job Summary"], ["required_education", "Required Education"],
  ["preferred_education", "Preferred Education"], ["required_experience", "Required Experience"],
  ["work_setting", "Work Setting"], ["employment_type", "Employment Type"],
  ["remote_status", "Remote / Hybrid / On-site"], ["pay_range", "Salary Range"], ["start_date", "Start Date"],
];
const LIST_FIELDS = [
  ["alternative_titles", "Alternative Job Titles"], ["required_licenses", "Required Licenses"],
  ["required_certifications", "Required Certifications"], ["required_skills", "Required Skills"],
  ["preferred_skills", "Preferred Skills"], ["populations_served", "Populations Served"],
  ["required_keywords", "Required Keywords"], ["preferred_keywords", "Preferred Keywords"],
  ["mandatory_requirements", "Mandatory Qualifications"], ["preferred_qualifications", "Preferred Qualifications"],
  ["disqualifying_criteria", "Disqualifying Criteria"],
];
const asList = (a) => (Array.isArray(a) ? a : (a ? String(a).split(",").map((x) => x.trim()).filter(Boolean) : []));

const BUCKETS = [
  { key: "high", label: "High Matches", sub: "90–100", color: "hsl(46 65% 52%)" },
  { key: "strong", label: "Strong Matches", sub: "80–89", color: "hsl(175 77% 26%)" },
  { key: "possible", label: "Possible Matches", sub: "70–79", color: "hsl(200 60% 45%)" },
  { key: "transferable", label: "Potential Transferable Candidates", sub: "cross-profession fit", color: "hsl(262 52% 55%)" },
  { key: "not_qualified", label: "Not Qualified", sub: "below threshold", color: "hsl(215 16% 55%)" },
];

function ReviewCard({ label, value, isList, needsReview, onChange }) {
  const [editing, setEditing] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const display = isList ? asList(value).join(", ") : (value || "");
  const empty = isList ? asList(value).length === 0 : !value;

  return (
    <div data-testid={`review-card-${label}`} className={`border rounded-xl p-4 bg-white transition-colors ${confirmed ? "border-emerald-300 bg-emerald-50/30" : needsReview ? "border-amber-300" : "border-border"}`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
        <div className="flex items-center gap-1">
          {needsReview && !confirmed && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"><AlertTriangle size={10} className="mr-1" />Needs review</Badge>}
          {confirmed && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"><Check size={10} className="mr-1" />Confirmed</Badge>}
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          {label === "Job Summary" ? (
            <Textarea autoFocus value={display} onChange={(e) => onChange(isList ? asList(e.target.value) : e.target.value)} rows={3} data-testid={`edit-input-${label}`} />
          ) : (
            <Input autoFocus value={display} onChange={(e) => onChange(isList ? asList(e.target.value) : e.target.value)} data-testid={`edit-input-${label}`} placeholder={isList ? "comma-separated" : ""} />
          )}
          <Button size="sm" variant="secondary" onClick={() => { setEditing(false); setConfirmed(true); }}>Done</Button>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <p className={`text-sm ${empty ? "italic text-muted-foreground" : "text-foreground/90"}`}>{empty ? "Not found — please add or leave blank" : display}</p>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setConfirmed(true)} data-testid={`confirm-${label}`} title="Confirm" className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600 transition-colors"><CheckCircle2 size={15} /></button>
            <button onClick={() => setEditing(true)} data-testid={`edit-${label}`} title="Edit" className="p-1.5 rounded hover:bg-secondary transition-colors"><Pencil size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NewPosition() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("upload");
  const [pasteText, setPasteText] = useState("");
  const [dragging, setDragging] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [tag, setTag] = useState({ client: "", school_location: "", recruiter_assigned: "", priority: "Normal", bill_rate: "", pay_rate: "", number_of_openings: 1, internal_notes: "" });
  const [savedId, setSavedId] = useState(null);
  const [matchData, setMatchData] = useState(null);

  const analyze = async (formData) => {
    setStep(2);
    try {
      const { data } = await api.post("/positions/extract", formData, { headers: { "Content-Type": "multipart/form-data" } });
      pollExtract(data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not analyze the job description");
      setStep(1);
    }
  };

  const pollExtract = async (jobId, attempts = 0) => {
    try {
      const { data } = await api.get(`/positions/extract/${jobId}`);
      if (data.status === "ready") { setExtracted(data.data); setStep(3); return; }
      if (data.status === "error") { toast.error(data.error || "Could not analyze the job description"); setStep(1); return; }
    } catch (e) {
      if (attempts > 48) { toast.error("Analysis timed out — please try again."); setStep(1); return; }
    }
    if (attempts > 48) { toast.error("Analysis timed out — please try again."); setStep(1); return; }
    setTimeout(() => pollExtract(jobId, attempts + 1), 2500);
  };
  const onFile = (file) => { if (!file) return; if (!/\.(pdf|docx)$/i.test(file.name)) { toast.error("Upload a PDF or DOCX file"); return; } const fd = new FormData(); fd.append("file", file); analyze(fd); };
  const onPaste = () => { if (pasteText.trim().length < 40) { toast.error("Please paste a fuller job description"); return; } const fd = new FormData(); fd.append("text", pasteText); analyze(fd); };
  const setField = (k, v) => setExtracted((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!tag.client || !tag.school_location || !tag.recruiter_assigned) { toast.error("Client, School/Location and Recruiter are required"); return; }
    setStep(5);
    try {
      const payload = { ...extracted, ...tag, number_of_openings: Number(tag.number_of_openings) || 1,
        required_years_experience: Number(extracted.required_years_experience) || 0 };
      const { data } = await api.post("/positions", payload);
      setSavedId(data.id);
      pollMatches(data.id);
    } catch { toast.error("Failed to save position"); setStep(4); }
  };

  const pollMatches = async (id, attempts = 0) => {
    try {
      const { data } = await api.get(`/positions/${id}/matches`);
      if (data.matched > 0 || data.total_candidates === 0 || attempts > 25) { setMatchData(data); return; }
    } catch {}
    setTimeout(() => pollMatches(id, attempts + 1), 2500);
  };

  const needs = extracted?.needs_review || [];
  const isNeed = (label) => needs.some((n) => n.toLowerCase().includes(label.toLowerCase().split(" ")[0]));

  return (
    <div className="p-8 max-w-5xl">
      <button onClick={() => navigate("/positions")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"><ArrowLeft size={15} /> Back to positions</button>
      <div className="mb-6 fade-up">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--teal))] font-semibold flex items-center gap-1.5"><Sparkles size={13} /> AI Recruiting Assistant</div>
        <h1 className="font-head text-3xl font-semibold tracking-tight mt-1">New Position</h1>
        <p className="text-muted-foreground mt-2">Upload a job description — the AI does the data entry. You just verify.</p>
      </div>

      <div className="flex items-center gap-2 mb-8">
        {["Upload", "Analyze", "Review", "TAG Info", "Match"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= i + 1 ? "bg-[hsl(var(--teal))] text-white" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
            <span className={`text-xs ${step >= i + 1 ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s}</span>
            {i < 4 && <div className="w-6 h-px bg-border" />}
          </div>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="fade-up">
          <div className="flex gap-2 mb-4">
            <Button variant={mode === "upload" ? "default" : "outline"} onClick={() => setMode("upload")} data-testid="mode-upload" className={mode === "upload" ? "bg-[hsl(var(--teal))]" : ""}><UploadCloud size={16} className="mr-1.5" />Upload File</Button>
            <Button variant={mode === "paste" ? "default" : "outline"} onClick={() => setMode("paste")} data-testid="mode-paste" className={mode === "paste" ? "bg-[hsl(var(--teal))]" : ""}><ClipboardPaste size={16} className="mr-1.5" />Paste Text</Button>
          </div>
          <h2 className="font-head font-bold text-xl">Upload Job Description</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-4">Upload a PDF, Word document, or paste the job description below.</p>
          {mode === "upload" ? (
            <div data-testid="jd-dropzone" onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById("jd-file").click()}
              className={`border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-colors ${dragging ? "border-[hsl(var(--teal))] bg-[hsl(var(--teal))]/5" : "border-border bg-white hover:border-[hsl(var(--teal))]/50"}`}>
              <input id="jd-file" type="file" accept=".pdf,.docx" className="hidden" data-testid="jd-file-input" onChange={(e) => onFile(e.target.files[0])} />
              <UploadCloud size={44} className="mx-auto text-[hsl(var(--teal))]" />
              <p className="font-head font-bold text-lg mt-4">Drag & drop the job description</p>
              <p className="text-sm text-muted-foreground mt-1">PDF or DOCX · or click to browse</p>
            </div>
          ) : (
            <div className="bg-white border border-border rounded-2xl p-4">
              <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={12} placeholder="Paste the full job description here…" data-testid="jd-paste-input" className="resize-none" />
              <Button onClick={onPaste} data-testid="analyze-paste-btn" className="mt-3 bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90"><Sparkles size={16} className="mr-1.5" />Analyze Job Description</Button>
            </div>
          )}
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="text-center py-24 fade-up">
          <Loader2 size={48} className="mx-auto text-[hsl(var(--teal))] animate-spin" />
          <h2 className="font-head font-bold text-2xl mt-6">Analyzing Job Description…</h2>
          <p className="text-muted-foreground mt-2">The AI is extracting titles, requirements, skills and qualifications.</p>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && extracted && (
        <div className="fade-up">
          <div className="flex items-center justify-between bg-[hsl(var(--teal))]/8 border border-[hsl(var(--teal))]/20 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={24} className="text-[hsl(var(--teal))]" />
              <div><div className="font-head font-bold text-lg">AI Analysis Complete</div><div className="text-sm text-muted-foreground">Verify or correct the extracted fields below — no retyping needed.</div></div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Confidence</div>
              <div className={`font-head text-3xl font-extrabold ${extracted.confidence >= 75 ? "text-[hsl(var(--teal))]" : "text-[hsl(var(--gold))]"}`} data-testid="confidence-score">{extracted.confidence}%</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {STR_FIELDS.map(([k, label]) => <ReviewCard key={k} label={label} value={extracted[k]} needsReview={isNeed(label)} onChange={(v) => setField(k, v)} />)}
            {LIST_FIELDS.map(([k, label]) => <ReviewCard key={k} label={label} value={extracted[k]} isList needsReview={isNeed(label)} onChange={(v) => setField(k, v)} />)}
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft size={15} className="mr-1" />Start over</Button>
            <Button onClick={() => setStep(4)} data-testid="to-tag-info-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">Continue <ArrowRight size={15} className="ml-1" /></Button>
          </div>
        </div>
      )}

      {/* STEP 4 */}
      {step === 4 && (
        <div className="fade-up max-w-2xl">
          <h2 className="font-head font-bold text-xl">TAG Internal Information</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-5">Only the details the AI can't read from a job description.</p>
          <div className="grid md:grid-cols-2 gap-4 bg-white border border-border rounded-xl p-6">
            {[["client", "Client *"], ["school_location", "School / Location *"], ["recruiter_assigned", "Recruiter Assigned *"], ["bill_rate", "Bill Rate"], ["pay_rate", "Pay Rate"]].map(([k, label]) => (
              <div key={k}><Label className="text-xs">{label}</Label><Input value={tag[k]} onChange={(e) => setTag({ ...tag, [k]: e.target.value })} data-testid={`tag-${k}`} className="mt-1" /></div>
            ))}
            <div><Label className="text-xs">Priority</Label>
              <Select value={tag.priority} onValueChange={(v) => setTag({ ...tag, priority: v })}>
                <SelectTrigger className="mt-1" data-testid="tag-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["Urgent", "High", "Normal", "Future"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Number of Openings</Label><Input type="number" min="1" value={tag.number_of_openings} onChange={(e) => setTag({ ...tag, number_of_openings: e.target.value })} data-testid="tag-openings" className="mt-1" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Internal Notes (optional)</Label><Textarea value={tag.internal_notes} onChange={(e) => setTag({ ...tag, internal_notes: e.target.value })} data-testid="tag-notes" className="mt-1" rows={3} /></div>
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft size={15} className="mr-1" />Back</Button>
            <Button onClick={save} data-testid="save-position-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">Save Position & Match Candidates</Button>
          </div>
        </div>
      )}

      {/* STEP 5 */}
      {step === 5 && (
        <div className="fade-up">
          <div className="text-center py-10">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <h2 className="font-head font-bold text-2xl mt-4">Position Created Successfully</h2>
            {!matchData ? (
              <div className="mt-8">
                <div className="flex items-center justify-center gap-2 text-[hsl(var(--teal))]"><Search size={18} className="animate-pulse" /><span className="font-medium">Searching Candidate Database…</span></div>
                <div className="mt-4 w-64 mx-auto h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-[hsl(var(--teal))] rounded-full animate-pulse" style={{ width: "70%" }} /></div>
                <p className="text-sm text-muted-foreground mt-3">Comparing this position against every candidate.</p>
              </div>
            ) : (
              <p className="text-muted-foreground mt-2">Matched against {matchData.total_candidates} candidate{matchData.total_candidates !== 1 && "s"} in the database.</p>
            )}
          </div>

          {matchData && (
            <div className="space-y-6">
              {BUCKETS.map(({ key, label, sub, color }) => {
                const items = matchData.buckets[key] || [];
                return (
                  <div key={key}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="h-3 w-3 rounded-full" style={{ background: color }} />
                      <h3 className="font-head font-bold text-lg">{label}</h3>
                      <span className="text-sm text-muted-foreground">· {sub}</span>
                      <Badge variant="secondary" className="ml-1">{items.length}</Badge>
                    </div>
                    {items.length === 0 ? <p className="text-sm text-muted-foreground pl-5">No candidates in this tier.</p> : (
                      <div className="grid md:grid-cols-2 gap-3">
                        {items.map((m) => (
                          <div key={m.candidate_id} data-testid={`match-cand-${m.candidate_id}`} onClick={() => navigate(`/candidates/${m.candidate_id}`)}
                            className="card-hover flex items-center gap-3 bg-white border border-border rounded-xl p-4 cursor-pointer">
                            <ScoreRing score={m.score || 0} size={46} />
                            <div className="min-w-0">
                              <div className="font-medium truncate">{m.candidate_name || "Unnamed"}</div>
                              <div className="text-xs text-muted-foreground truncate">{m.candidate_title || m.candidate_classification}</div>
                              <Badge variant="outline" className="mt-1 text-[10px] capitalize">{m.classification}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex gap-3 pt-2">
                <Button onClick={() => navigate("/positions")} variant="outline">Done</Button>
                <Button onClick={() => navigate(`/candidates`)} className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">View all candidates</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
